import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { type MarkdownPost } from "../utils/data";
import { useData } from "../context/DataContext";
import BlogCard from "../components/BlogCard";
import Breadcrumb from "../components/Breadcrumb";

export default function Blog() {
    // Global Data
    const { data: globalData } = useData();
    const header = globalData.blog.header;
    const posts = globalData.blog.list;

    // Loading State
    const isLoading = !header;

    // Expandable Filter State
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Search & Filter State
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // displayHeader...
    const displayHeader = header || {
        header: { badge: 'Blog', title: 'Düşünceler', description: 'Yazılım, tasarım ve teknoloji üzerine notlar.' }
    };


    const radiusClass = "rounded-[20px]";
    const borderClass = "border-2 border-[#2A2A2A] hover:border-[#404040]";

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.04,
                delayChildren: 0
            }
        }
    };

    const itemVariants: any = {
        hidden: { opacity: 0, y: 20, scale: 0.98 },
        show: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.4,
                ease: "backOut"
            }
        }
    };

    // Compute unique tags
    const allTags = Array.from(new Set(posts.flatMap(p => p.frontmatter.tag ? [p.frontmatter.tag] : []))).sort();

    // Filter Logic
    const filteredPosts = posts.filter(post => {
        const matchesSearch = (post.frontmatter.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            post.frontmatter.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesTag = selectedTag ? post.frontmatter.tag === selectedTag : true;

        return matchesSearch && matchesTag;
    });

    return (
        <motion.div
            initial="hidden"
            animate="show"
            exit={{ opacity: 0 }}
            variants={containerVariants}
            className="min-h-screen flex flex-col items-center py-24 px-4 md:py-16 pb-32"
        >
            <div className="w-full max-w-4xl mx-auto h-auto">


                {/* 2. CONTROL ROW (Breadcrumb + Search/Filter) */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mb-8">

                    {/* LEFT: Breadcrumb Box */}
                    <div className={`bg-[var(--card-bg)] ${radiusClass} ${borderClass} px-8 flex items-center h-[54px]`}>
                        <Breadcrumb items={[{ label: "Blog" }]} />
                    </div>

                    {/* RIGHT: Search & Filter Box */}
                    <div className="flex items-center gap-3 relative z-30">

                        {/* Search Input */}
                        <div className="relative h-[54px] w-full md:w-64">
                            <input
                                type="text"
                                placeholder="Yazılarda ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`w-full h-full bg-[var(--card-bg)] ${radiusClass} ${borderClass} text-white text-sm px-4 pr-10 placeholder-gray-500 focus:outline-none focus:border-[var(--accent)] transition-colors`}
                            />
                            <div className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-500 pointer-events-none">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>

                        {/* Category Dropdown */}
                        <div className="relative h-[54px] min-w-[160px]">
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className={`w-full h-full bg-[var(--card-bg)] ${radiusClass} ${borderClass} text-xs font-bold px-4 flex items-center justify-between transition-colors ${isDropdownOpen ? 'border-[var(--accent)] text-white' : 'border-[#2A2A2A] text-gray-300 hover:border-[#444]'
                                    }`}
                            >
                                <span className="truncate">{selectedTag || "Kategori Seçin"}</span>
                                <svg className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {isDropdownOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute top-full right-0 mt-2 w-full bg-[#151515] border-2 border-[#2A2A2A] rounded-xl shadow-2xl overflow-hidden z-50 p-1.5"
                                        >
                                            <button
                                                onClick={() => { setSelectedTag(null); setIsDropdownOpen(false); }}
                                                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all border-2 mb-1 ${selectedTag === null ? 'bg-[var(--accent)]/10 border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-gray-400 hover:bg-[#222] hover:text-white hover:border-[#333]'}`}
                                            >
                                                Tümü
                                            </button>
                                            {allTags.map(tag => (
                                                <button
                                                    key={tag}
                                                    onClick={() => { setSelectedTag(tag); setIsDropdownOpen(false); }}
                                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all border-2 mb-1 ${selectedTag === tag ? 'bg-[var(--accent)]/10 border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-gray-400 hover:bg-[#222] hover:text-white hover:border-[#333]'}`}
                                                >
                                                    {tag}
                                                </button>
                                            ))}
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                {/* 2. BLOG POST LIST */}
                <motion.div
                    className="flex flex-col gap-3"
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                >
                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <>
                                {[1, 2, 3].map((i) => (
                                    <motion.div
                                        key={`skeleton-${i}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="h-[120px] bg-[#111] rounded-[20px] border border-[#222] animate-pulse p-4 flex items-center gap-6"
                                    >
                                        {/* Left Box Skeleton */}
                                        <div className="w-20 h-20 bg-[#222] rounded-2xl opacity-30 shrink-0" />

                                        {/* Right Content Skeleton */}
                                        <div className="flex-grow space-y-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-16 h-4 bg-[#222] rounded opacity-40" />
                                                <div className="w-1 h-1 bg-[#222] rounded-full opacity-40" />
                                                <div className="w-24 h-4 bg-[#222] rounded opacity-40" />
                                            </div>
                                            <div className="w-2/3 h-6 bg-[#222] rounded-lg opacity-60" />
                                            <div className="w-1/2 h-4 bg-[#222] rounded opacity-30" />
                                        </div>
                                    </motion.div>
                                ))}
                            </>
                        ) : (
                            filteredPosts.map((post, i) => (
                                <motion.div
                                    key={post.slug}
                                    variants={itemVariants}
                                    style={{ willChange: "transform, opacity" }}
                                >
                                    <BlogCard post={post} index={i} />
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>
                </motion.div>

                {!isLoading && filteredPosts.length === 0 && (
                    <div className="py-20 text-center text-gray-500">
                        <p className="text-sm">Aradığınız kriterlere uygun yazı bulunamadı.</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
